import type { ToolDefinition, ToolType } from "../types";

const SVG_IMAGE_OPTIMIZER_TOOL_TYPE = {
  label: "SVG Optimisation Tool",
  icon: "FileCode",
} as const satisfies ToolType;

const svgImageOptimizerTool: ToolDefinition = {
  slug: "svg-image-optimizer",
  name: "SVG Optimizer: Reduce SVG File Size & Minify Online for Free",
  title: "SVG Image Optimizer",
  description:
    "Optimize SVG files online for free. Reduce SVG file size by removing metadata and cleaning code without losing quality. Fast, secure, and lossless SVG minifier.",
  longDescription:
    "Shrink your SVG images without sacrificing visual quality. Our tool strips away unnecessary metadata, hidden comments, and bloated code exports from tools like Figma or Illustrator, ensuring your web assets load instantly and efficiently.",
  toolType: {
    label: "Optimization Tool",
    icon: "Zap",
  },
  heroBadges: [
    "100% Free",
    "No Watermark",
    "No Account Required",
    "Works on Mobile",
    "Secure Processing",
  ],
  category: "Optimization Tools",
  categorySlug: "image-optimization-tools",
  href: "/tools/svg-image-optimizer",
  api: "/api/v1/optimize-svg",
  keywords: [
    "snappy svg optimizer tool",
    "compress svg files online",
    "reduce svg file size",
    "check svg vector bloat",
    "clean svg markup code",
    "fast svg minify app",
    "decrease svg image size",
    "analyze svg compression limits",
    "optimize vector graphics web",
    "make inline vectors smaller",
  ],
  pageContent: {
    sections: {
      steps: {
        title: "How to Optimize Your SVG in Seconds",
        description:
          "Follow these simple steps to minify your vector graphics for peak web performance.",
      },
      benefits: {
        title: "Why Use Our SVG Minifier?",
        description:
          "Our tool doesn't just make files smaller; it makes them cleaner and more efficient for modern browsers.",
      },
      faqs: {
        title: "Frequently Asked Questions",
        description:
          "Learn more about SVG optimization, browser compatibility, and vector file size reduction.",
      },
    },
    intro: {
      title: "Why Design Tool SVG Exports Are Larger Than They Need to Be",
      paragraphs: [
        "Vector graphics are the gold standard for modern web design, offering crisp visuals at any resolution. However, when you export a file from Adobe Illustrator, Figma, or Inkscape, the resulting code is often bloated with technical debt. These tools prioritize the ability to re-edit the file, so they embed a massive amount of hidden data: XML namespaces, editor metadata, hidden layers, and incredibly long ID strings. This extra 'junk' can increase a file's size by 50% or more, slowing down your website's load times and hurting your SEO performance.",
        "Our Snappy-Fix SVG optimizer solves this by parsing your vector code and stripping out everything that isn't required for rendering. Using the advanced Scour engine, we perform a deep clean of the markup. This includes rounding coordinates to sensible decimal places, shortening IDs, and removing empty groups. The result is a **lossless visual output**—the image looks exactly the same, but the file size is significantly reduced. Whether you are building a React component library or optimizing a company logo for a WordPress site, this tool ensures your vectors are as light as possible.",
      ],
      note: "We never alter the visual paths or colors of your image. Every optimization is purely code-based and lossless.",
    },
    introBlocks: [
      {
        kind: "text",
        title: "Optimize SVG for Web Performance and PageSpeed",
        level: "h2",
        paragraphs: [
          "In 2026, web performance is a critical ranking factor. Large SVG payloads, especially when used as inline vectors, can bloat your HTML and delay the First Contentful Paint (FCP). By using our **fast SVG minify app**, you can ensure that your icons and illustrations load instantly. This is particularly important for mobile users on slower connections where every kilobyte counts.",
          "Beyond just file size, clean SVG code is easier to maintain. If you have ever tried to minify inline SVG in a React or Vue component, you know how messy exported code can be. Our tool removes non-standard attributes and ensures the XML is valid and concise. This makes your codebase cleaner and prevents weird rendering bugs that can occur when browsers encounter proprietary editor tags.",
        ],
        note: "Optimized SVGs are much safer to use inline, as they contain fewer elements for the browser to parse.",
      },
      {
        kind: "table",
        tables: [
          {
            title: "Comparison: Raw vs. Optimized SVG",
            description:
              "See what changes when you run your vector graphics through our optimizer.",
            columns: [
              "Feature",
              "Raw Export (Illustrator/Figma)",
              "Optimized (Snappy-Fix)",
            ],
            rows: [
              {
                Feature: "Metadata",
                "Raw Export (Illustrator/Figma)":
                  "Includes Creator, App Version, XMP",
                "Optimized (Snappy-Fix)": "Completely Removed",
              },
              {
                Feature: "ID Attributes",
                "Raw Export (Illustrator/Figma)":
                  "Long, descriptive, or random IDs",
                "Optimized (Snappy-Fix)": "Shortened (e.g., 'a', 'b', 'c')",
              },
              {
                Feature: "Whitespace",
                "Raw Export (Illustrator/Figma)":
                  "Pretty-printed with tabs/spaces",
                "Optimized (Snappy-Fix)": "Stripped (Single line code)",
              },
              {
                Feature: "Coordinate Precision",
                "Raw Export (Illustrator/Figma)": "Up to 10 decimal places",
                "Optimized (Snappy-Fix)": "Rounded to 3 decimal places",
              },
              {
                Feature: "File Size",
                "Raw Export (Illustrator/Figma)": "100% (Baseline)",
                "Optimized (Snappy-Fix)": "30% - 70% Smaller",
              },
            ],
            note: "Despite these removals, the visual output remains 100% identical in all modern browsers.",
            emphasisColumns: ["Optimized (Snappy-Fix)"],
          },
        ],
      },
      {
        kind: "callouts",
        groups: [
          {
            title: "Core Cleanup Operations",
            layout: "list",
            iconColor: "primary",
            items: [
              {
                label: "Remove XML Namespaces",
                icon: "Code",
                description:
                  "Strips out xmlns attributes used by specific editors like Inkscape or Illustrator that have no effect on browser rendering.",
                accent: "primary",
              },
              {
                label: "Path Data Simplification",
                icon: "ArrowLeftRight",
                description:
                  "Cleans up messy SVG path data by removing redundant commands and rounding coordinate values for a smaller payload.",
                accent: "primary",
              },
              {
                label: "ID Minification",
                icon: "Type",
                description:
                  "Renames long IDs to short single characters to save space while maintaining internal references.",
                accent: "primary",
              },
              {
                label: "Comment Stripping",
                icon: "MessageSquareOff",
                description:
                  "Removes all developer comments and editor-specific notes embedded within the code.",
                accent: "primary",
              },
            ],
          },
        ],
      },
    ],
    faqs: [
      {
        question: "How do I reduce an SVG file size without losing quality?",
        answer:
          "The best way is to use an SVG optimizer that performs lossless minification. Our tool removes non-visual elements like metadata, comments, and extra whitespace, while rounding coordinates to reduce character count. This ensures the visual paths and colors remain exactly the same while the file size drops significantly.",
      },
      {
        question: "Why is my exported SVG file so large?",
        answer:
          "Design tools like Adobe Illustrator and Figma include 'editor state' in their exports. This allows you to reopen the file later and see layers or specific tool settings. For the web, this data is useless bloat. A typical SVG export contains a large header full of metadata that can be safely stripped.",
      },
      {
        question: "Does compressing an SVG change its dimensions?",
        answer:
          "No. The viewBox and dimensions of your SVG remain untouched. The optimization happens at the code level, affecting only the complexity and verbosity of the markup, not the physical scale or aspect ratio of the graphic.",
      },
      {
        question: "How do I clean up messy SVG code safely?",
        answer:
          "Simply upload your SVG to our tool. It uses the Scour engine to validate the XML structure before cleaning it. It automatically removes empty tags, unused IDs, and redundant groups without breaking the rendering logic.",
      },
      {
        question: "Why are Figma SVG exports bigger than expected?",
        answer:
          "Figma often adds extra metadata and complex nested groups to its exports to maintain layer hierarchy. Our tool flattens unnecessary groups and removes the Figma-specific attributes to make the SVG web-ready.",
      },
      {
        question:
          "Is it better to use an optimized SVG or PNG for a website logo?",
        answer:
          "In almost all cases, an optimized SVG is better for logos. SVGs are resolution-independent (they never look blurry) and are usually much smaller in file size than a high-resolution PNG, especially after being minified by our tool.",
      },
      {
        question: "Can I use this tool for Android Studio vector graphics?",
        answer:
          "Yes! Android's VectorDrawable format is based on SVG. By optimizing your SVG here first, you ensure the code is clean and compatible before importing it into Android Studio, which can prevent 'unsupported attribute' errors.",
      },
    ],
    benefits: [
      {
        icon: "ShieldCheck",
        title: "Lossless Compression",
        description:
          "We use the industry-standard Scour engine to ensure that your graphics look exactly the same before and after optimization.",
      },
      {
        icon: "FastForward",
        title: "Boost PageSpeed",
        description:
          "Smaller files mean faster downloads. Reduce your SVG payload size to improve your Core Web Vitals and overall user experience.",
      },
      {
        icon: "Code",
        title: "Developer Friendly",
        description:
          "Get rid of proprietary editor tags and namespaces. Receive clean, valid SVG code that is easy to drop directly into HTML or React components.",
      },
    ],
    steps: [
      {
        number: "01",
        title: "Export your SVG from your design tool",
        description:
          "Export your SVG from Figma, Illustrator, Inkscape, or any other tool using standard SVG export. No special export settings are needed—the optimizer handles cleanup regardless of how verbose the original export is.",
      },
      {
        number: "02",
        title: "Upload the SVG file",
        description:
          "Drag and drop your SVG file onto the upload zone or click to browse. A preview renders immediately so you can confirm the correct file loaded. Only SVG files are accepted (image/svg+xml).",
      },
      {
        number: "03",
        title: "Click Optimize Image",
        description:
          "Hit the button and watch the progress bar. The tool sends your SVG to the server, runs scour with metadata removal, comment stripping, ID shortening, and coordinate rounding.",
      },
      {
        number: "04",
        title: "Download and deploy",
        description:
          "Click 'Download Optimized Image' to save the clean SVG. Deploy it to your website, design system, or component library. The file renders identically in all browsers—just faster and lighter.",
      },
    ],
    useCases: [
      {
        label: "Web Development",
        icon: "Globe",
        description:
          "Optimizing icons and illustrations for better performance on high-traffic websites and applications.",
        accent: "primary",
      },
      {
        label: "Logo Deployment",
        icon: "Stamp",
        description:
          "Ensuring brand logos are crisp, small, and render perfectly across all screen sizes and email clients.",
        accent: "primary",
      },
      {
        label: "Design Systems",
        icon: "Layout",
        description:
          "Cleaning up Figma or Sketch exports to create a standardized, lightweight SVG icon library for developers.",
        accent: "primary",
      },
    ],
    audiences: [
      {
        label: "UI/UX Designers",
        icon: "PenTool",
        description:
          "Designers who want to provide developers with clean, production-ready assets without manual code cleaning.",
        accent: "accent",
      },
      {
        label: "Frontend Developers",
        icon: "User",
        description:
          "Developers looking to reduce bundle sizes and improve lighthouse scores by minifying inline SVG components.",
        accent: "accent",
      },
      {
        label: "SEO Specialists",
        icon: "Search",
        description:
          "Professionals optimizing site assets to improve load times and technical SEO rankings.",
        accent: "accent",
      },
    ],
  },
};

export default svgImageOptimizerTool;
export { svgImageOptimizerTool };
